/**
 * SegmentedTabs — Apple iOS-style segmented control.
 *
 * Rounded container with subtle gray background.
 * Active tab has white background with soft shadow.
 * Supports optional leading icon and notification badge per tab.
 */

import type { ComponentType } from 'react';
import { ds } from '../tokens';

export interface SegmentedTab {
  id: string;
  label: string;
  icon?: ComponentType<{ className?: string; strokeWidth?: string | number }>;
  badge?: number;
}

export interface SegmentedTabsProps {
  tabs: SegmentedTab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

/** Reusable tab set for card/document entry forms (ID cards, insurance, etc.) */
export const CARD_ENTRY_TABS: SegmentedTab[] = [
  { id: 'upload', label: 'Upload Card' },
  { id: 'manual', label: 'Enter Details' },
];

export function SegmentedTabs({ tabs, activeTab, onTabChange, className = '' }: SegmentedTabsProps) {
  const { segmentedControl: sc } = ds;

  return (
    <div
      className={`${sc.container} ${className}`}
      style={{ backgroundColor: sc.containerBg }}
      role="tablist"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === activeTab;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onTabChange(tab.id)}
            className={sc.tab.base}
            style={{
              color: isActive ? sc.tab.active : sc.tab.inactive,
              backgroundColor: isActive ? sc.tab.activeBg : 'transparent',
              boxShadow: isActive ? sc.tab.shadow : 'none',
            }}
          >
            {Icon && <Icon className={ds.icon.sm} strokeWidth={2} />}
            {tab.label}
            {tab.badge != null && tab.badge > 0 && (
              <span
                className={sc.badge.base}
                style={{ backgroundColor: sc.badge.bg }}
              >
                {tab.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
